import type { LngLatBoundsLike } from 'maplibre-gl'
import type { GeoMetricRow, MetricOption } from '../../types/geoAnalytics'

import { DIVERGING_RAMP, SEQUENTIAL_RAMP, SUPPRESSED_FILL } from './viz/mapRamps'
export { DIVERGING_RAMP, SEQUENTIAL_RAMP, SUPPRESSED_FILL }

/** Mirrors backend LeanCalculator.FormatLean. */
export function formatLean(value: number): string {
  const rounded = Math.round(value * 10) / 10
  if (Math.abs(rounded) < 0.05) return 'EVEN'
  return rounded > 0 ? `D+${rounded.toFixed(1)}` : `R+${Math.abs(rounded).toFixed(1)}`
}

const VOTER_SUPPRESSED_PREFIXES = ['turnout.', 'dropoff.', 'universe.', 'electorate.']

/** Legend caption for the suppressed swatch: voter-derived metrics have a k-anonymity
 *  floor, but ACS/election-results metrics (and terrain) have no voter-count basis. */
export function suppressionCaption(metricKey: string): string {
  return VOTER_SUPPRESSED_PREFIXES.some(p => metricKey.startsWith(p))
    ? 'Insufficient data (fewer than 10 voters)'
    : 'No data (unavailable for this area)'
}

export function formatMetricValue(option: MetricOption, value: number | null, suppressed: boolean): string {
  if (suppressed) return 'Insufficient data'
  if (value == null) return 'No data'
  switch (option.format) {
    case 'lean':
      return formatLean(value)
    case 'percent':
      return `${value.toFixed(1)}%`
    case 'count':
      return value.toLocaleString('en-US')
  }
}

export function buildChoroplethGeoJson(
  units: GeoJSON.FeatureCollection,
  rows: GeoMetricRow[],
  option: MetricOption,
): GeoJSON.FeatureCollection {
  const byUnit = new Map(rows.map(r => [r.geoUnitId, r]))
  return {
    type: 'FeatureCollection',
    features: units.features.map(feature => {
      const id = feature.properties?.id as string | undefined
      const row = id ? byUnit.get(id) : undefined
      const hasValue = !!row && row.value != null
      const suppressed = !!row?.suppressed
      return {
        ...feature,
        properties: {
          ...feature.properties,
          value: row?.value ?? null,
          suppressed,
          hasValue,
          displayValue: row
            ? formatMetricValue(option, row.value, suppressed)
            : 'No data',
        },
      }
    }),
  }
}

/** Walk every coordinate of a Polygon/MultiPolygon/Point FeatureCollection into a
 *  [[minLng,minLat],[maxLng,maxLat]] box for map.fitBounds. Returns null when the
 *  collection is empty or carries no positional geometry (nothing to fit to). */
export function featureCollectionBounds(
  fc: GeoJSON.FeatureCollection | null | undefined,
): LngLatBoundsLike | null {
  if (!fc || fc.features.length === 0) return null
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity

  const visit = (position: number[]): void => {
    const [lng, lat] = position
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }

  // Recurse the nested coordinate arrays without enumerating every geometry type.
  const walk = (coords: unknown): void => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number') {
      visit(coords as number[])
      return
    }
    for (const child of coords) walk(child)
  }

  for (const feature of fc.features) {
    const geom = feature.geometry
    if (geom && 'coordinates' in geom) walk(geom.coordinates)
  }

  if (minLng === Infinity) return null
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

export function computeDomain(option: MetricOption, rows: GeoMetricRow[]): [number, number] {
  if (option.format !== 'count') return option.domain
  const max = rows.reduce((m, r) => (r.value != null && r.value > m ? r.value : m), 0)
  return [0, Math.max(max, 10)]
}

export function fillColorExpression(option: MetricOption, domain: [number, number]): unknown[] {
  const interpolate =
    option.kind === 'diverging'
      ? [
          'interpolate', ['linear'], ['get', 'value'],
          domain[0], DIVERGING_RAMP[0],
          (domain[0] + domain[1]) / 2, DIVERGING_RAMP[1],
          domain[1], DIVERGING_RAMP[2],
        ]
      : [
          'interpolate', ['linear'], ['get', 'value'],
          domain[0], SEQUENTIAL_RAMP[0],
          domain[1], SEQUENTIAL_RAMP[1],
        ]
  return [
    'case',
    ['!', ['to-boolean', ['get', 'hasValue']]],
    ['case', ['to-boolean', ['get', 'suppressed']], SUPPRESSED_FILL, 'rgba(0,0,0,0)'],
    interpolate,
  ]
}
